/**
 * Reusable predicate functions for action visibility.
 *
 * These predicates are composable building blocks that can be combined
 * to create complex visibility rules in a declarative way.
 */

import { ActionContext } from "./types";
import {
  canApplyPreivewBackground,
  cardBackgroundColor,
  cleanedTagInput,
  colorValue,
  isAsset,
  isDownloadable,
  previewBackgroundColor,
} from "./actionHelpers";
import { ActionPredicate } from "./types";
import { AssetType, ItemType, KitPermission, SpacePermission } from "@thenounproject/lingo-core";

// =============================================================================
// PREDICATE COMBINATORS
// =============================================================================

export const always: ActionPredicate = () => true;
export const never: ActionPredicate = () => false;

/**
 * Combines multiple predicates with AND logic.
 * All predicates must return true for the result to be true.
 */
export function and(...predicates: ActionPredicate[]): ActionPredicate {
  return (ctx: ActionContext) => predicates.every(p => p(ctx));
}

/**
 * Combines multiple predicates with OR logic.
 * At least one predicate must return true for the result to be true.
 */
export function or(...predicates: ActionPredicate[]): ActionPredicate {
  return (ctx: ActionContext) => predicates.some(p => p(ctx));
}

/**
 * Negates a predicate.
 */
export function not(predicate: ActionPredicate): ActionPredicate {
  return (ctx: ActionContext) => !predicate(ctx);
}

// =============================================================================
// CAPABILITY PREDICATES
// =============================================================================

/** User has edit permission for the current context */
export const canEdit: ActionPredicate = ctx => ctx.capabilities.canEdit;

/** Space has enhanced customization feature enabled */
export const canAccessLibrary: ActionPredicate = ctx =>
  ctx.location.space.access?.permissions.includes(SpacePermission.viewAssetLibrary);

// =============================================================================
// LOCATION PREDICATES
// =============================================================================

/** Currently viewing a kit */
export const inKit: ActionPredicate = ctx => ctx.location.inKit;

/** Currently viewing the asset library */
export const inLibrary: ActionPredicate = ctx => ctx.location.inLibrary;

/** Currently viewing a gallery */
export const inGallery: ActionPredicate = ctx => ctx.location.inGallery;

/** Currently viewing the asset detail */
export const inAssetDetail: ActionPredicate = ctx => ctx.location.inAssetDetail;

// =============================================================================
// SELECTION COUNT PREDICATES
// =============================================================================

/** Has exactly one item selected */
export const hasSingleSelection: ActionPredicate = ctx => ctx.selection.all.length === 1;

/** Has at least one kit item selected (items with .item property) */
export const hasItems: ActionPredicate = ctx => ctx.selection.items.length > 0;

/** Has exactly one kit item selected */
export const hasSingleItem: ActionPredicate = ctx => ctx.selection.items.length === 1;

/** Has at least one asset selected (either item.type == asset or asset in the library) */
export const hasAssets: ActionPredicate = ctx =>
  ctx.selection.assets.length > 0 && ctx.selection.assets.some(isAsset);

/** Has asset items (items that are of type asset) */
export const hasAssetItems: ActionPredicate = ctx => ctx.selection.assetItems.length > 0;

/** Has exactly one asset item selected */
export const hasSingleAssetItem: ActionPredicate = ctx => ctx.selection.assetItems.length === 1;

/** Has at least one guide item selected */
export const hasGuideItems: ActionPredicate = ctx => ctx.selection.guideItems.length > 0;

/** Has at least one gallery item selected */
export const hasGalleryItems: ActionPredicate = ctx => ctx.selection.galleryItems.length > 0;

// =============================================================================
// SELECTION TYPE PREDICATES
// =============================================================================

/** Single selected item is an asset (either item.type == asset or asset in the library) */
export const hasSingleAsset: ActionPredicate = ctx => {
  if (ctx.selection.all.length !== 1) return false;
  const single = ctx.selection.all[0];
  return isAsset(single);
};

/** Single selected item is a gallery */
export const singleItemIsGallery: ActionPredicate = ctx => {
  if (ctx.selection.items.length !== 1) return false;
  return ctx.selection.items[0]?.item?.type === ItemType.gallery;
};

/** Single selected item is a guide */
export const singleItemIsGuide: ActionPredicate = ctx => {
  if (ctx.selection.items.length !== 1) return false;
  return ctx.selection.items[0]?.item?.type === ItemType.guide;
};

/** Single selected item is a note */
export const singleItemIsNote: ActionPredicate = ctx => {
  if (ctx.selection.items.length !== 1) return false;
  return ctx.selection.items[0]?.item?.type === ItemType.note;
};

/** Single selected asset is a color */
export const singleAssetIsColor: ActionPredicate = ctx => {
  if (ctx.selection.all.length !== 1) return false;
  return ctx.selection.all[0]?.asset?.type === AssetType.color;
};

/** Single selected asset is a URL */
export const singleAssetIsURL: ActionPredicate = ctx => {
  if (ctx.selection.all.length !== 1) return false;
  return ctx.selection.all[0]?.asset?.type === AssetType.URL;
};

/** Selection has at least one asset with Figma source */
export const selectionHasFigmaAssets: ActionPredicate = ctx =>
  ctx.selection.assets.some(i => Boolean(i.asset?.meta?.figma?.url));

export const hasDownloadableAssets: ActionPredicate = ctx =>
  ctx.selection.assetItems.some(isDownloadable);

// =============================================================================
// STATUS PREDICATES
// =============================================================================

/** Selection has at least one item at version 0 (editable) */
export const selectionHasVersion0Items: ActionPredicate = ctx =>
  ctx.selection.items.some(i => i.item?.version === 0);

// =============================================================================
// CONTENT PREDICATES
// =============================================================================

/** Single selected item has text content */
export const singleItemHasTextContent: ActionPredicate = ctx => {
  if (ctx.selection.items.length !== 1) return false;
  const single = ctx.selection.items[0];

  return (
    Boolean(single?.item?.data?.content?.length) ||
    Boolean(single?.item?.displayProperties?.caption?.length)
  );
};

/** Single selected item is a text-editable type */
export const singleItemIsTextType: ActionPredicate = ctx => {
  if (ctx.selection.items.length !== 1) return false;
  const textTypes = [ItemType.codeSnippet, ItemType.note, ItemType.heading, ItemType.guide];
  return textTypes.includes(ctx.selection.items[0]?.item?.type);
};

/** Single item has caption */
export const singleItemHasCaption: ActionPredicate = ctx => {
  if (ctx.selection.assetItems.length !== 1) return false;
  return Boolean(ctx.selection.assetItems[0]?.item?.displayProperties?.caption?.length);
};

/** Single item has no caption */
export const singleItemHasNoCaption: ActionPredicate = ctx => {
  if (ctx.selection.assetItems.length !== 1) return false;
  const caption = ctx.selection.assetItems[0]?.item?.displayProperties?.caption;
  return caption == null || caption.length === 0;
};

/** Single asset has a custom preview/thumbnail */
export const singleAssetHasCustomPreview: ActionPredicate = ctx => {
  if (ctx.selection.all.length !== 1) return false;
  return Boolean(ctx.selection.all[0]?.asset?.meta?.preview?.id);
};

export const hasPreviewBackgroundCompatibleAssets = (ctx: ActionContext) =>
  canApplyPreivewBackground(ctx);

// =============================================================================
// PERMISSION PREDICATES
// =============================================================================

/** User has permission to manage share links for the kit */
export const canManageShareLinks: ActionPredicate = ctx =>
  Boolean(ctx.location.kit?.access?.permissions.includes(KitPermission.manageSharelinks));

/** User has permission to view advanced analytics */
export const canViewAdvancedAnalytics: ActionPredicate = ctx =>
  Boolean(ctx.location.space?.access?.permissions.includes(SpacePermission.viewAdvancedAnalytics));

/** Space has export_management feature + user has manageExports on kit (items) or space (library) */
export const canManageExports: ActionPredicate = ctx => {
  if (!ctx.location.space?.features?.includes("export_management")) return false;
  if (ctx.selection.items.length > 0)
    return Boolean(ctx.location.kit?.access?.permissions.includes(KitPermission.manageExports));
  return Boolean(ctx.location.space?.access?.permissions.includes(SpacePermission.manageExports));
};

export const hasResizableItems: ActionPredicate = ctx =>
  Boolean(ctx.selection.resizableItems.length);

export const hasLayoutCompatibleItems = or(hasAssetItems, hasGalleryItems, hasGuideItems);

// =============================================================================
// INPUT PREDICATES
// =============================================================================

/** User input is a valid card background preset */
export const hasCardColorInput: ActionPredicate = ctx => !!cardBackgroundColor(ctx);

/** User input is a valid preview background preset */
export const hasPreviewColorInput: ActionPredicate = ctx => !!previewBackgroundColor(ctx);

/** User input is a valid hex color (#RGB or #RRGGBB) */
export const hasColorInput: ActionPredicate = ctx => !!colorValue(ctx);

export const canAddTag: ActionPredicate = ctx => {
  const cleanedTag = cleanedTagInput(ctx);
  return (
    cleanedTag.length > 2 &&
    ctx.selection.assets.some(i => isAsset(i) && !i.asset?.keywords.split(",").includes(cleanedTag))
  );
};

export const canRemoveTag: ActionPredicate = ctx => {
  const cleanedTag = cleanedTagInput(ctx);
  return (
    cleanedTag.length &&
    ctx.selection.assets.some(i => isAsset(i) && i.asset?.keywords.split(",").includes(cleanedTag))
  );
};
